import Drawflow from "drawflow";
import { buildOrthogonalPath } from "./geometry";
import { computeAutomaticLayout } from "./layout";
import { mergeEditableNodeData } from "./nodeData";
import { appendEditorMetadata, extractDrawflowGraph } from "./state";
import {
  clampZoom,
  DRAWFLOW_FIT_PADDING,
  normalizeViewport,
} from "./viewport";

export default class OmniviewDrawflow extends Drawflow {
  constructor(...args) {
    super(...args);
    this.connectionRefreshFrame = null;
    this.layoutFrames = new Set();
    this.nodeResizeObserver = null;
    this.observedNodes = new Set();
    this.selectedConnection = null;
    this.connectionListenersRegistered = false;
    this.onConnectionSelected = this.handleConnectionSelected.bind(this);
    this.onConnectionUnselected = this.clearSelectedConnection.bind(this);
    this.onConnectionRemoved = this.handleConnectionRemoved.bind(this);
  }

  createCurvature(startX, startY, endX, endY) {
    return buildOrthogonalPath(startX, startY, endX, endY);
  }

  start() {
    super.start();
    this.registerConnectionSelectionListeners();
    this.initializeConnectionResizeObserver();
    this.observeNodeSizes();
  }

  addNode(...args) {
    const nodeId = super.addNode(...args);
    this.scheduleConnectionRefresh();

    return nodeId;
  }

  addNodeOutput(nodeId) {
    super.addNodeOutput(nodeId);
    this.scheduleConnectionRefresh();
  }

  removeNodeOutput(nodeId, outputClass) {
    super.removeNodeOutput(nodeId, outputClass);
    this.scheduleConnectionRefresh();
  }

  import(data, notify = true) {
    return this.importState(data, notify);
  }

  importState(data, notify = true) {
    const graph = extractDrawflowGraph(data);
    const viewport = normalizeViewport(
      data?._editor?.viewport,
      this.zoom_min,
      this.zoom_max
    );

    super.import(graph, notify);
    this.observeNodeSizes();
    this.scheduleConnectionRefresh();

    return this.afterLayout(() => {
      if (viewport) {
        this.applyViewport(viewport);
      } else {
        this.fitView();
      }
      this.refreshConnections();
    });
  }

  exportState() {
    const graph = super.export();

    return appendEditorMetadata(graph, this.getViewport());
  }

  commitNodeData(nodeId, patch) {
    const node = this.getNodeFromId(nodeId);
    if (!node) return null;

    const data = mergeEditableNodeData(node.data, patch);
    this.updateNodeDataFromId(nodeId, data);
    this.scheduleConnectionRefresh();

    return data;
  }

  getViewport() {
    return {
      zoom: this.zoom,
      x: this.canvas_x,
      y: this.canvas_y,
    };
  }

  applyViewport(viewport) {
    const normalized = normalizeViewport(viewport, this.zoom_min, this.zoom_max);
    if (!normalized) return false;

    this.zoom = normalized.zoom;
    this.zoom_last_value = normalized.zoom;
    this.canvas_x = normalized.x;
    this.canvas_y = normalized.y;
    this.precanvas.style.transform = this.viewportTransform();
    this.dispatch("zoom", this.zoom);
    this.dispatch("translate", { x: this.canvas_x, y: this.canvas_y });

    return true;
  }

  setZoom(zoom, anchor = {}) {
    const nextZoom = clampZoom(zoom, this.zoom_min, this.zoom_max);
    if (nextZoom === this.zoom) return this.zoom;

    const anchorX = Number.isFinite(anchor.x)
      ? anchor.x
      : this.container.clientWidth / 2;
    const anchorY = Number.isFinite(anchor.y)
      ? anchor.y
      : this.container.clientHeight / 2;
    const worldX = (anchorX - this.canvas_x) / this.zoom;
    const worldY = (anchorY - this.canvas_y) / this.zoom;

    this.zoom = nextZoom;
    this.zoom_last_value = nextZoom;
    this.canvas_x = anchorX - worldX * nextZoom;
    this.canvas_y = anchorY - worldY * nextZoom;
    this.precanvas.style.transform = this.viewportTransform();
    this.dispatch("zoom", this.zoom);
    this.dispatch("translate", { x: this.canvas_x, y: this.canvas_y });

    return this.zoom;
  }

  zoomIn() {
    return this.setZoom(this.zoom + this.zoom_value);
  }

  zoomOut() {
    return this.setZoom(this.zoom - this.zoom_value);
  }

  /**
   * Zooms with the mouse wheel while keeping the content under the pointer fixed.
   * Drawflow calls this method from the wheel listener registered in start().
   */
  zoom_enter(event) {
    if (!event || event.deltaY === 0) return this.zoom;

    event.preventDefault();
    const containerRect = this.container.getBoundingClientRect();
    const anchor = {
      x: event.clientX - containerRect.left,
      y: event.clientY - containerRect.top,
    };
    const direction = event.deltaY < 0 ? 1 : -1;

    return this.setZoom(this.zoom + direction * this.zoom_value, anchor);
  }

  zoom_in() {
    return this.zoomIn();
  }

  zoom_out() {
    return this.zoomOut();
  }

  zoom_reset() {
    return this.setZoom(1);
  }

  fitView(padding = DRAWFLOW_FIT_PADDING) {
    const nodes = Array.from(this.precanvas.querySelectorAll(".drawflow-node"));
    if (!nodes.length) {
      this.applyViewport({ zoom: 1, x: 0, y: 0 });
      return this.getViewport();
    }

    const bounds = nodes.reduce(
      (result, node) => ({
        left: Math.min(result.left, node.offsetLeft),
        top: Math.min(result.top, node.offsetTop),
        right: Math.max(result.right, node.offsetLeft + node.offsetWidth),
        bottom: Math.max(result.bottom, node.offsetTop + node.offsetHeight),
      }),
      {
        left: Number.POSITIVE_INFINITY,
        top: Number.POSITIVE_INFINITY,
        right: Number.NEGATIVE_INFINITY,
        bottom: Number.NEGATIVE_INFINITY,
      }
    );
    const contentWidth = Math.max(1, bounds.right - bounds.left);
    const contentHeight = Math.max(1, bounds.bottom - bounds.top);
    const availableWidth = Math.max(1, this.container.clientWidth - padding * 2);
    const availableHeight = Math.max(1, this.container.clientHeight - padding * 2);
    const zoom = clampZoom(
      Math.min(1, availableWidth / contentWidth, availableHeight / contentHeight),
      this.zoom_min,
      this.zoom_max
    );
    const x = (this.container.clientWidth - contentWidth * zoom) / 2 - bounds.left * zoom;
    const y = (this.container.clientHeight - contentHeight * zoom) / 2 - bounds.top * zoom;

    this.applyViewport({ zoom, x, y });
    return this.getViewport();
  }

  /**
   * Repositions the current module using a deterministic left-to-right layout.
   * @returns {Promise<{zoom: number, x: number, y: number}>} The fitted viewport.
   */
  async arrangeNodes(options = {}) {
    await this.afterLayout(() => {});
    const moduleData = this.drawflow?.drawflow?.[this.module]?.data ?? {};
    const nodeIds = Object.keys(moduleData);
    if (!nodeIds.length) return this.getViewport();

    const dimensions = nodeIds.reduce((result, nodeId) => {
      const element = this.container.querySelector(`#node-${nodeId}`);
      if (element) {
        result[nodeId] = {
          width: element.offsetWidth,
          height: element.offsetHeight,
        };
      }
      return result;
    }, {});
    const { positions } = computeAutomaticLayout(moduleData, dimensions, options);

    Object.entries(positions).forEach(([nodeId, position]) => {
      const element = this.container.querySelector(`#node-${nodeId}`);
      moduleData[nodeId].pos_x = position.x;
      moduleData[nodeId].pos_y = position.y;
      if (element) {
        element.style.left = `${position.x}px`;
        element.style.top = `${position.y}px`;
      }
    });

    await this.afterLayout(() => {
      this.refreshConnections();
      this.fitView();
    });

    return this.getViewport();
  }

  viewportTransform() {
    return `translate(${this.canvas_x}px, ${this.canvas_y}px) scale(${this.zoom})`;
  }

  afterLayout(callback) {
    return new Promise(resolve => {
      const firstFrame = window.requestAnimationFrame(() => {
        this.layoutFrames.delete(firstFrame);
        const secondFrame = window.requestAnimationFrame(() => {
          this.layoutFrames.delete(secondFrame);
          callback();
          resolve();
        });
        this.layoutFrames.add(secondFrame);
      });
      this.layoutFrames.add(firstFrame);
    });
  }

  removeNodeId(nodeId) {
    const node = this.container.querySelector(`#${nodeId}`);
    if (node && this.nodeResizeObserver) {
      this.nodeResizeObserver.unobserve(node);
      this.observedNodes.delete(node);
    }
    super.removeNodeId(nodeId);
    this.scheduleConnectionRefresh();
  }

  registerConnectionSelectionListeners() {
    if (this.connectionListenersRegistered) return;

    this.on("connectionSelected", this.onConnectionSelected);
    this.on("connectionUnselected", this.onConnectionUnselected);
    this.on("connectionRemoved", this.onConnectionRemoved);
    this.connectionListenersRegistered = true;
  }

  handleConnectionSelected(connection) {
    const element = this.findConnectionElement(connection);
    if (!element) return;

    this.clearSelectedConnection();
    this.selectedConnection = element;
    element.classList.add("connection--selected");
    this.precanvas.appendChild(element);
  }

  handleConnectionRemoved() {
    if (this.selectedConnection && !this.selectedConnection.isConnected) {
      this.clearSelectedConnection();
    }
  }

  clearSelectedConnection() {
    this.selectedConnection?.classList.remove("connection--selected");
    this.selectedConnection = null;
  }

  findConnectionElement(connection) {
    if (!connection || !this.precanvas) return null;

    return Array.from(this.precanvas.querySelectorAll(".connection")).find(element =>
      element.classList.contains(`node_in_node-${connection.input_id}`)
      && element.classList.contains(`node_out_node-${connection.output_id}`)
      && element.classList.contains(connection.output_class)
      && element.classList.contains(connection.input_class)
    ) || null;
  }

  initializeConnectionResizeObserver() {
    if (typeof window.ResizeObserver !== "function") return;

    this.nodeResizeObserver = new window.ResizeObserver(() => {
      this.scheduleConnectionRefresh();
    });
    this.nodeResizeObserver.observe(this.container);
  }

  observeNodeSizes() {
    if (!this.nodeResizeObserver) return;

    this.container.querySelectorAll(".drawflow-node").forEach(node => {
      if (this.observedNodes.has(node)) return;
      this.observedNodes.add(node);
      this.nodeResizeObserver.observe(node);
    });
  }

  scheduleConnectionRefresh() {
    if (this.connectionRefreshFrame !== null) return;

    this.connectionRefreshFrame = window.requestAnimationFrame(() => {
      this.connectionRefreshFrame = null;
      this.refreshConnections();
    });
  }

  refreshConnections() {
    this.observeNodeSizes();
    const moduleData = this.drawflow?.drawflow?.[this.module]?.data ?? {};
    Object.keys(moduleData).forEach(nodeId => {
      this.updateConnectionNodes(`node-${nodeId}`);
    });
  }

  destroy() {
    if (this.connectionRefreshFrame !== null) {
      window.cancelAnimationFrame(this.connectionRefreshFrame);
      this.connectionRefreshFrame = null;
    }
    this.layoutFrames.forEach(frame => window.cancelAnimationFrame(frame));
    this.layoutFrames.clear();
    this.nodeResizeObserver?.disconnect();
    this.nodeResizeObserver = null;
    this.observedNodes.clear();
    this.clearSelectedConnection();
    if (this.connectionListenersRegistered) {
      this.removeListener("connectionSelected", this.onConnectionSelected);
      this.removeListener("connectionUnselected", this.onConnectionUnselected);
      this.removeListener("connectionRemoved", this.onConnectionRemoved);
      this.connectionListenersRegistered = false;
    }
  }
}
